/**
 * Chart.js dataset helpers for Project Overview Worker Hours / Concrete charts.
 */

import { Chart } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

import { CHART_COLORS, DATE_TIME_CONFIG } from "../../../../../config";
import {
  countWeeksInInclusiveRange,
  formatShortMonthDay,
  formatWeekOfLabel,
  getDateInLocalTZFromDateStr,
  getShiftedDate,
  getWeekStart,
  today,
  toLocalDateKey,
} from "../../../../../utils/datetime";
import { withTypePrefix } from "../metricsHelpers";

// Register once for Overview (and any other) charts that import this module.
Chart.register(annotationPlugin);

/**
 * Chart.js plugin: horizontally offset dataset drawing inside the plot area only.
 * Axes, tick labels, and legend stay fixed (CSS transform cannot do this).
 *
 * @param {() => number} getOffsetX - Current pan offset in CSS pixels.
 * @returns {Object} Chart.js plugin descriptor.
 */
export function createOverviewChartPanPlugin(getOffsetX) {
  return {
    id: "overviewChartPan",
    /**
     * Clip to the plot area and translate datasets for the chevron pan.
     *
     * @param {import("chart.js").Chart} chart
     * @returns {void}
     */
    beforeDatasetsDraw(chart) {
      const offsetX = Number(getOffsetX()) || 0;
      if (!offsetX) return;
      const { ctx, chartArea } = chart;
      const { left, right, top, bottom } = chartArea;
      ctx.save();
      ctx.beginPath();
      ctx.rect(left, top, right - left, bottom - top);
      ctx.clip();
      ctx.translate(offsetX, 0);
      chart.$overviewPanClipped = true;
    },
    /**
     * Restore canvas state after dataset drawing.
     *
     * @param {import("chart.js").Chart} chart
     * @returns {void}
     */
    afterDatasetsDraw(chart) {
      if (!chart.$overviewPanClipped) return;
      chart.$overviewPanClipped = false;
      chart.ctx.restore();
    },
  };
}

/** Vertical marker for the current day / week bucket. */
const TODAY_LINE_COLOR = "#dc2626";

/** Stacked regular hours (matches Overview tile styling). */
export const WORKER_HOURS_REGULAR_COLOR = CHART_COLORS.labor;
/** Stacked overtime hours (matches Overview overtime accent). */
export const WORKER_HOURS_OVERTIME_COLOR = CHART_COLORS.secondary;
/** Stacked double-time hours (top of bar, above overtime). */
export const WORKER_HOURS_DOUBLE_TIME_COLOR = CHART_COLORS.subcontractors;
/** Workers line on the secondary axis. */
export const WORKER_HOURS_WORKERS_COLOR = CHART_COLORS.primary;
/** Concrete volume bars (fallback / single-series). */
export const CONCRETE_VOLUME_COLOR = CHART_COLORS.primary;

/** Palette for stacked mix-design segments (in-filter / Type-matched). */
const CONCRETE_MIX_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.labor,
  CHART_COLORS.subcontractors,
  "#71b93e",
  "#26cdc4",
  "#8b5cf6",
  "#a5a5a5",
];

/** Monochrome stack for concrete volume outside the active Type filter (50% opacity). */
const CONCRETE_OUT_FILTER_COLOR = "rgba(148, 163, 184, 0.5)";

const WEEK_START = DATE_TIME_CONFIG.START_OF_WEEK.default;

/** Chart bucket modes for Overview metrics charts. */
export const CHART_GRANULARITY_DAILY = "daily";
export const CHART_GRANULARITY_WEEKLY = "weekly";

/**
 * Prefer Daily when the visible window (or All-range data) spans fewer than
 * this many weeks. Keep in sync with metricsHelpers default-range threshold.
 */
const MIN_WEEKS_FOR_WEEKLY_DEFAULT = 8;

/**
 * Count distinct Sunday-start weeks represented in day rows.
 *
 * @param {Array<Object>|null|undefined} days
 * @returns {number}
 */
export function countOverviewChartWeeks(days) {
  const weekKeys = new Set();
  (days || []).forEach((day) => {
    if (!day?.date) return;
    const dayDate = getDateInLocalTZFromDateStr(day.date);
    weekKeys.add(toLocalDateKey(getWeekStart(dayDate, WEEK_START)));
  });
  return weekKeys.size;
}

/**
 * Default chart bucket: Weekly when the visible window (or open-ended data)
 * spans ≥ 8 weeks; otherwise Daily.
 *
 * Open start/end (default: all history → today) uses activity-day week count.
 *
 * @param {{start: string|null, end: string|null}|null} range
 * @param {Array<Object>|null|undefined} [days] - Aggregated day rows for open-range week-span check.
 * @returns {"daily"|"weekly"}
 */
export function getDefaultOverviewChartGranularity(range, days = null) {
  if (range?.start && range?.end) {
    return countWeeksInInclusiveRange(range.start, range.end, WEEK_START) >=
      MIN_WEEKS_FOR_WEEKLY_DEFAULT
      ? CHART_GRANULARITY_WEEKLY
      : CHART_GRANULARITY_DAILY;
  }
  if (countOverviewChartWeeks(days) < MIN_WEEKS_FOR_WEEKLY_DEFAULT) {
    return CHART_GRANULARITY_DAILY;
  }
  return CHART_GRANULARITY_WEEKLY;
}

/**
 * Expand sparse day rows across an inclusive YYYY-MM-DD range (zero-fill).
 * When range has no bounds, return days as-is (activity days only).
 *
 * @param {Array<Object>} days - Aggregated day rows keyed by ``date``.
 * @param {{start: string|null, end: string|null}|null} range
 * @param {(dateKey: string) => Object} emptyDay - Factory for a zero day row.
 * @returns {Array<Object>}
 */
export function fillDaysForRange(days, range, emptyDay) {
  const byDate = Object.fromEntries(
    (days || []).map((day) => [day.date, day])
  );

  // Zero-fill only when both bounds are set; partial/open ranges keep activity days.
  if (!range?.start || !range?.end) {
    return (days || []).slice().sort((a, b) => a.date.localeCompare(b.date));
  }

  const filled = [];
  let cursor = getDateInLocalTZFromDateStr(range.start);
  const end = getDateInLocalTZFromDateStr(range.end);
  while (cursor.getTime() <= end.getTime()) {
    const dateKey = toLocalDateKey(cursor);
    filled.push(byDate[dateKey] || emptyDay(dateKey));
    cursor = getShiftedDate(cursor, 1);
  }
  return filled;
}

/**
 * Format a day key as a two-line Chart.js category label.
 *
 * Chart.js renders each array entry on its own line, e.g.:
 *   Mon
 *   Jul 20
 *
 * @param {string} dateKey - YYYY-MM-DD.
 * @returns {[string, string]}
 */
export function formatChartDayLabel(dateKey) {
  const date = getDateInLocalTZFromDateStr(dateKey);
  return [
    date.toLocaleDateString("en-US", { weekday: "short" }),
    formatShortMonthDay(date),
  ];
}

/**
 * Format a week-start key like the schedule week header.
 *
 * @param {string} weekStartKey - YYYY-MM-DD week start.
 * @returns {string}
 */
export function formatChartWeekLabel(weekStartKey) {
  return formatWeekOfLabel(weekStartKey);
}

/**
 * Vertical red "today" line when today's day/week bucket is among chart categories.
 *
 * @param {string[]|null|undefined} categoryKeys - YYYY-MM-DD keys aligned with labels.
 * @param {boolean} [weekly=false] - When true, match the week that contains today.
 * @returns {{annotations: Object}}
 */
export function getTodayLineAnnotation(categoryKeys, weekly = false) {
  const todayKey = toLocalDateKey(today());
  const targetKey = weekly
    ? toLocalDateKey(
        getWeekStart(getDateInLocalTZFromDateStr(todayKey), WEEK_START)
      )
    : todayKey;
  const index = (categoryKeys || []).indexOf(targetKey);
  if (index < 0) {
    return { annotations: {} };
  }

  return {
    annotations: {
      todayLine: {
        type: "line",
        xMin: index,
        xMax: index,
        borderColor: TODAY_LINE_COLOR,
        borderWidth: 2,
      },
    },
  };
}

/**
 * Shared Chart.js options for Overview metric charts.
 *
 * @param {Object} [options]
 * @param {string} [options.tooltipSuffix=""] - Suffix appended to tooltip values.
 * @param {boolean} [options.indexInteraction=false] - Enable index-mode tooltips.
 * @returns {Object}
 */
function getOverviewChartBaseOptions({
  tooltipSuffix = "",
  indexInteraction = false,
} = {}) {
  const config = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      annotation: {
        annotations: {},
      },
      tooltip: {
        callbacks: {
          // Two-line day labels → "Mon, Jul 20" (Chart.js default is "Mon,Jul 20").
          title(items) {
            const item = items?.[0];
            if (!item) return "";
            const raw = item.chart?.data?.labels?.[item.dataIndex];
            return Array.isArray(raw) ? raw.join(", ") : item.label;
          },
          label(context) {
            return `${context.dataset.label}: ${context.parsed.y}${tooltipSuffix}`;
          },
        },
      },
    },
  };

  if (indexInteraction) {
    config.interaction = {
      mode: "index",
      intersect: false,
    };
  }

  return config;
}

/** Shared stacked x-axis config for Overview charts. */
function getOverviewStackedXScale() {
  return {
    stacked: true,
    grid: { display: false },
    ticks: {
      maxRotation: 0,
      minRotation: 0,
      // Prefer a label on every category; Chart.js thins to every N when narrow.
      autoSkip: true,
      autoSkipPadding: 6,
      font: {
        size: 10,
      },
    },
  };
}

/** Shared stacked primary y-axis config for Overview charts. */
function getOverviewStackedYScale(title) {
  return {
    stacked: true,
    beginAtZero: true,
    title: {
      display: true,
      text: title,
    },
    grid: {
      color: "rgba(148, 163, 184, 0.35)",
    },
  };
}

/**
 * Roll day rows into Sunday-start week buckets for All-range charts.
 *
 * @param {Array<Object>} days
 * @param {"hours"|"concrete"} kind
 * @returns {Array<Object>}
 */
function aggregateDaysToWeeks(days, kind) {
  const weeks = {};

  (days || []).forEach((day) => {
    const dayDate = getDateInLocalTZFromDateStr(day.date);
    const weekStartKey = toLocalDateKey(getWeekStart(dayDate, WEEK_START));
    if (!weeks[weekStartKey]) {
      if (kind === "hours") {
        weeks[weekStartKey] = {
          date: weekStartKey,
          regularHours: 0,
          overtimeHours: 0,
          doubleTimeHours: 0,
          workerIds: new Set(),
        };
      } else {
        weeks[weekStartKey] = {
          date: weekStartKey,
          yards: 0,
          pourCount: 0,
          yardsByMix: {},
          yardsByMixInFilter: {},
          yardsByMixOutFilter: {},
        };
      }
    }

    const bucket = weeks[weekStartKey];
    if (kind === "hours") {
      bucket.regularHours += Number(day.regularHours) || 0;
      bucket.overtimeHours += Number(day.overtimeHours) || 0;
      bucket.doubleTimeHours += Number(day.doubleTimeHours) || 0;
      (day.workerIds || []).forEach((id) => bucket.workerIds.add(id));
    } else {
      bucket.yards += Number(day.yards) || 0;
      bucket.pourCount += Number(day.pourCount) || 0;
      Object.entries(day.yardsByMix || {}).forEach(([mixKey, yards]) => {
        bucket.yardsByMix[mixKey] =
          (bucket.yardsByMix[mixKey] || 0) + (Number(yards) || 0);
      });
      Object.entries(day.yardsByMixInFilter || {}).forEach(([mixKey, yards]) => {
        bucket.yardsByMixInFilter[mixKey] =
          (bucket.yardsByMixInFilter[mixKey] || 0) + (Number(yards) || 0);
      });
      Object.entries(day.yardsByMixOutFilter || {}).forEach(
        ([mixKey, yards]) => {
          bucket.yardsByMixOutFilter[mixKey] =
            (bucket.yardsByMixOutFilter[mixKey] || 0) + (Number(yards) || 0);
        }
      );
    }
  });

  return Object.keys(weeks)
    .sort()
    .map((key) => {
      const bucket = weeks[key];
      if (kind === "hours") {
        return {
          date: bucket.date,
          regularHours: bucket.regularHours,
          overtimeHours: bucket.overtimeHours,
          doubleTimeHours: bucket.doubleTimeHours,
          workerCount: bucket.workerIds.size,
        };
      }
      return {
        date: bucket.date,
        yards: bucket.yards,
        pourCount: bucket.pourCount,
        yardsByMix: bucket.yardsByMix,
        yardsByMixInFilter: bucket.yardsByMixInFilter,
        yardsByMixOutFilter: bucket.yardsByMixOutFilter,
      };
    });
}

/**
 * Build the visible series + label formatter for the active range / granularity.
 *
 * @param {Array<Object>|null|undefined} days
 * @param {{start: string|null, end: string|null}|null} range
 * @param {(dateKey: string) => Object} emptyDay
 * @param {"hours"|"concrete"} kind
 * @param {"daily"|"weekly"} granularity
 * @returns {{series: Array<Object>, formatLabel: (dateKey: string) => string, weekly: boolean}}
 */
function buildChartSeries(days, range, emptyDay, kind, granularity) {
  const daily = fillDaysForRange(days, range, emptyDay);
  const weekly = granularity === CHART_GRANULARITY_WEEKLY;
  if (!weekly) {
    return {
      series: daily,
      formatLabel: formatChartDayLabel,
      weekly: false,
    };
  }
  return {
    series: aggregateDaysToWeeks(daily, kind),
    formatLabel: formatChartWeekLabel,
    weekly: true,
  };
}

/**
 * Build Chart.js data for Worker Hours (stacked RT/OT/DT bars + workers line).
 *
 * Stack order is RT (bottom) → OT → DT (top), matching timesheet buckets.
 * Multi-type selections should pass amalgamated ``days`` with ``typeLabel="All"``.
 *
 * @param {Array<Object>|null|undefined} days - Aggregated day rows.
 * @param {{start: string|null, end: string|null}|null} range
 * @param {"daily"|"weekly"} [granularity] - Bucket size (defaults from range).
 * @param {string} [typeLabel="All"] - Active Type name for dataset labels.
 * @returns {{labels: Array, datasets: Array<Object>, weekly: boolean, categoryKeys: string[]}}
 */
export function buildWorkerHoursChartData(
  days,
  range = null,
  granularity = getDefaultOverviewChartGranularity(range),
  typeLabel = "All"
) {
  const { series, formatLabel, weekly } = buildChartSeries(
    days,
    range,
    (dateKey) => ({
      date: dateKey,
      regularHours: 0,
      overtimeHours: 0,
      doubleTimeHours: 0,
      workerCount: 0,
      workerIds: [],
    }),
    "hours",
    granularity
  );

  return {
    weekly,
    categoryKeys: series.map((day) => day.date),
    labels: series.map((day) => formatLabel(day.date)),
    datasets: [
      {
        type: "bar",
        label: withTypePrefix(typeLabel, "Regular Time"),
        data: series.map((day) => Number(day.regularHours) || 0),
        backgroundColor: WORKER_HOURS_REGULAR_COLOR,
        borderColor: WORKER_HOURS_REGULAR_COLOR,
        stack: "hours",
        yAxisID: "y",
        order: 2,
      },
      {
        type: "bar",
        label: withTypePrefix(typeLabel, "Overtime"),
        data: series.map((day) => Number(day.overtimeHours) || 0),
        backgroundColor: WORKER_HOURS_OVERTIME_COLOR,
        borderColor: WORKER_HOURS_OVERTIME_COLOR,
        stack: "hours",
        yAxisID: "y",
        order: 2,
      },
      {
        type: "bar",
        label: withTypePrefix(typeLabel, "Double Time"),
        data: series.map((day) => Number(day.doubleTimeHours) || 0),
        backgroundColor: WORKER_HOURS_DOUBLE_TIME_COLOR,
        borderColor: WORKER_HOURS_DOUBLE_TIME_COLOR,
        stack: "hours",
        yAxisID: "y",
        order: 2,
      },
      {
        type: "line",
        label: withTypePrefix(typeLabel, "Workers"),
        data: series.map((day) => Number(day.workerCount) || 0),
        borderColor: WORKER_HOURS_WORKERS_COLOR,
        backgroundColor: WORKER_HOURS_WORKERS_COLOR,
        yAxisID: "y1",
        // Discrete day/week counts — straight segments, no interpolation curves.
        tension: 0,
        pointRadius: weekly ? 2 : 3,
        pointHoverRadius: 5,
        borderWidth: 2,
        order: 1,
      },
    ],
  };
}

/**
 * Chart.js options for the Worker Hours dual-axis chart.
 *
 * @returns {Object}
 */
export function getWorkerHoursChartOptions() {
  return {
    ...getOverviewChartBaseOptions({ indexInteraction: true }),
    scales: {
      x: getOverviewStackedXScale(),
      y: {
        ...getOverviewStackedYScale("Hours"),
        position: "left",
      },
      y1: {
        position: "right",
        beginAtZero: true,
        grace: "10%",
        title: {
          display: true,
          text: "Workers",
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          precision: 0,
          stepSize: 1,
        },
      },
    },
  };
}

/**
 * Collect mix-design keys present in a concrete day series (in + out of Type).
 *
 * @param {Array<Object>} series
 * @returns {string[]}
 */
function collectConcreteMixKeys(series) {
  const keys = new Set();
  (series || []).forEach((day) => {
    Object.keys(day.yardsByMix || {}).forEach((mixKey) => keys.add(mixKey));
    Object.keys(day.yardsByMixInFilter || {}).forEach((mixKey) =>
      keys.add(mixKey)
    );
    Object.keys(day.yardsByMixOutFilter || {}).forEach((mixKey) =>
      keys.add(mixKey)
    );
  });
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
}

/**
 * Whether a concrete series has any out-of-Type volume to gray out.
 *
 * @param {Array<Object>} series
 * @returns {boolean}
 */
function concreteSeriesHasOutFilterVolume(series) {
  return (series || []).some((day) =>
    Object.values(day.yardsByMixOutFilter || {}).some(
      (yards) => (Number(yards) || 0) > 0
    )
  );
}

/**
 * Build Chart.js data for Concrete volume bars stacked by mix design.
 *
 * When Type is filtered, ``days`` should come from
 * {@link aggregateConcretePoursChartDays}: in-filter mixes keep color, and
 * out-of-filter volume is stacked in monochrome gray.
 *
 * @param {Array<Object>|null|undefined} days - Chart day rows (full Range split).
 * @param {{start: string|null, end: string|null}|null} range
 * @param {"daily"|"weekly"} [granularity] - Bucket size (defaults from range).
 * @param {string} [typeLabel="All"] - Active Type name for dataset labels.
 * @returns {{labels: Array, datasets: Array<Object>, weekly: boolean, categoryKeys: string[]}}
 */
export function buildConcretePoursChartData(
  days,
  range = null,
  granularity = getDefaultOverviewChartGranularity(range),
  typeLabel = "All"
) {
  const { series, formatLabel, weekly } = buildChartSeries(
    days,
    range,
    (dateKey) => ({
      date: dateKey,
      yards: 0,
      pourCount: 0,
      yardsByMix: {},
      yardsByMixInFilter: {},
      yardsByMixOutFilter: {},
    }),
    "concrete",
    granularity
  );

  const mixKeys = collectConcreteMixKeys(series);
  const showOutFilter = concreteSeriesHasOutFilterVolume(series);
  const datasets = [];

  if (mixKeys.length === 0) {
    datasets.push({
      label: withTypePrefix(typeLabel, "Volume (CY)"),
      data: series.map((day) => Number(day.yards) || 0),
      backgroundColor: CONCRETE_VOLUME_COLOR,
      borderColor: CONCRETE_VOLUME_COLOR,
      borderRadius: 2,
      stack: "volume",
    });
  } else {
    // Gray out-of-filter segments first (under), then colored in-filter on top.
    if (showOutFilter) {
      mixKeys.forEach((mixKey) => {
        const hasOut = series.some(
          (day) => (Number(day.yardsByMixOutFilter?.[mixKey]) || 0) > 0
        );
        if (!hasOut) return;
        datasets.push({
          // Kept for tooltips only — hidden from the legend via skipLegend.
          label: mixKey,
          skipLegend: true,
          data: series.map(
            (day) => Number(day.yardsByMixOutFilter?.[mixKey]) || 0
          ),
          backgroundColor: CONCRETE_OUT_FILTER_COLOR,
          borderColor: CONCRETE_OUT_FILTER_COLOR,
          borderRadius: 2,
          stack: "volume",
        });
      });
    }

    mixKeys.forEach((mixKey, index) => {
      const color = CONCRETE_MIX_COLORS[index % CONCRETE_MIX_COLORS.length];
      const inData = showOutFilter
        ? series.map(
            (day) => Number(day.yardsByMixInFilter?.[mixKey]) || 0
          )
        : series.map((day) => Number(day.yardsByMix?.[mixKey]) || 0);
      const hasIn = inData.some((value) => value > 0);
      if (!hasIn && showOutFilter) return;
      datasets.push({
        label: withTypePrefix(typeLabel, mixKey),
        data: inData,
        backgroundColor: color,
        borderColor: color,
        borderRadius: 2,
        stack: "volume",
      });
    });
  }

  return {
    weekly,
    categoryKeys: series.map((day) => day.date),
    labels: series.map((day) => formatLabel(day.date)),
    datasets,
  };
}

/**
 * Chart.js options for the Concrete volume bar chart.
 *
 * @returns {Object}
 */
export function getConcretePoursChartOptions() {
  const base = getOverviewChartBaseOptions({ tooltipSuffix: " CY" });
  return {
    ...base,
    plugins: {
      ...base.plugins,
      legend: {
        ...base.plugins.legend,
        labels: {
          ...base.plugins.legend.labels,
          /**
           * Hide out-of-Type (gray) datasets from the legend.
           * Chart.js passes ``chart.data`` as the second argument (not the chart).
           *
           * @param {Object} legendItem
           * @param {{datasets?: Array<Object>}} data
           * @returns {boolean}
           */
          filter(legendItem, data) {
            const dataset = data?.datasets?.[legendItem.datasetIndex];
            return !dataset?.skipLegend;
          },
        },
      },
    },
    scales: {
      x: getOverviewStackedXScale(),
      y: getOverviewStackedYScale("Cubic yards"),
    },
  };
}
